import { AssignPackageRecord } from '../../core/services/subscription.service';
import { downloadBlobFile } from '../../core/utils/browser-file.util';

export interface AssignPackageReceiptData {
  transactionId: string;
  packageName: string;
  price: string;
  isTrial: boolean;
  durationLabel: string;
  expireDate: string;
  userName: string;
  email: string;
  mobile: string;
  remarks: string;
  limits: Array<[string, string]>;
  createdOn: string;
  updatedOn: string;
}

type PdfImageFormat = 'PNG' | 'JPEG' | 'WEBP';

function sanitizeFileNamePart(value: string | null | undefined): string {
  const normalized = `${value ?? ''}`
    .trim()
    .replace(/[\\/:*?"<>|]+/g, ' ')
    .replace(/&/g, ' and ')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');
  return normalized || 'na';
}

function buildReceiptFileNameBase(data: AssignPackageReceiptData): string {
  const user = sanitizeFileNamePart(data.userName);
  const packageName = sanitizeFileNamePart(data.packageName);
  const expireDate = sanitizeFileNamePart(data.expireDate);
  const transactionId = sanitizeFileNamePart(data.transactionId);
  return `${user}-${packageName}-exp-${expireDate}-TRN-${transactionId}`;
}

function detectPdfImageFormatFromMime(mimeType: string): PdfImageFormat {
  const normalized = `${mimeType ?? ''}`.trim().toLowerCase();
  if (normalized.includes('png')) {
    return 'PNG';
  }
  if (normalized.includes('webp')) {
    return 'WEBP';
  }
  return 'JPEG';
}

async function loadImageForPdf(path: string): Promise<{ dataUrl: string; format: PdfImageFormat } | null> {
  try {
    if (!path) {
      return null;
    }
    const response = await fetch(path);
    if (!response.ok) {
      return null;
    }
    const blob = await response.blob();
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ''));
      reader.onerror = () => reject(new Error('Failed to read logo image.'));
      reader.readAsDataURL(blob);
    });
    return {
      dataUrl,
      format: detectPdfImageFormatFromMime(blob.type)
    };
  } catch {
    return null;
  }
}

export async function downloadAssignPackageReceiptPdf(data: AssignPackageReceiptData): Promise<void> {
  const { doc, fileName } = await buildAssignPackageReceiptPdfDoc(data);
  downloadBlobFile(doc.output('blob') as Blob, fileName);
}

export async function printAssignPackageReceiptPdf(data: AssignPackageReceiptData): Promise<void> {
  const { doc } = await buildAssignPackageReceiptPdfDoc(data);
  doc.autoPrint();
  const blobUrl = doc.output('bloburl');
  window.open(blobUrl, '_blank');
}

export async function buildAssignPackageReceiptPdfAttachment(data: AssignPackageReceiptData): Promise<{
  fileName: string;
  pdfBase64: string;
}> {
  const { doc, fileName } = await buildAssignPackageReceiptPdfDoc(data);
  const dataUri = doc.output('datauristring');
  const base64 = `${dataUri ?? ''}`.split(',')[1] || '';
  return {
    fileName,
    pdfBase64: base64
  };
}

async function buildAssignPackageReceiptPdfDoc(data: AssignPackageReceiptData): Promise<{ doc: any; fileName: string }> {
  const fileNameBase = buildReceiptFileNameBase(data);
  const fileName = `${fileNameBase}.pdf`;
  const [{ jsPDF }, logoImage] = await Promise.all([
    import('jspdf'),
    loadImageForPdf('/HTallyIcon.png')
  ]);

  const rows: Array<[string, string]> = [
    ['Transaction ID', data.transactionId],
    ['Package', data.packageName],
    ['Price', data.price],
    ['Trial', data.isTrial ? 'Yes' : 'No'],
    ['Duration', data.durationLabel],
    ['Expire Date', data.expireDate],
    ['User', data.userName],
    ['Email', data.email],
    ['Mobile', data.mobile]
  ];

  const receiptWidth = 226.8;
  const left = 10;
  const right = receiptWidth - 10;
  const contentWidth = right - left;
  const estimatedRowsHeight = rows.length * 20;
  const estimatedLimitHeight = (data.limits.length + 2) * 14;
  const remarkLines = Math.max(1, Math.ceil((data.remarks || 'N/A').length / 28));
  const receiptHeight = Math.max(370, 190 + estimatedRowsHeight + estimatedLimitHeight + (remarkLines * 10) + 70);

  const doc = new jsPDF({ unit: 'pt', format: [receiptWidth, receiptHeight] });
  doc.setProperties({
    title: fileNameBase,
    subject: 'Assign Package Receipt'
  });

  let y = 20;
  if (logoImage) {
    const logoWidth = 34;
    const logoHeight = 34;
    const logoX = (receiptWidth - logoWidth) / 2;
    doc.addImage(logoImage.dataUrl, logoImage.format, logoX, y - 2, logoWidth, logoHeight);
    y += 40;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(80, 93, 124);
  doc.text('Assign Package Receipt', receiptWidth / 2, y, { align: 'center' });

  y += 12;
  doc.setDrawColor(190, 200, 220);
  doc.line(left, y, right, y);
  y += 10;

  rows.forEach(([label, value]) => {
    const safeValue = value || 'N/A';
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(80, 93, 124);
    doc.setFontSize(8);
    doc.text(`${label}:`, left, y);
    doc.setTextColor(31, 38, 65);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    const wrappedValue = doc.splitTextToSize(safeValue, contentWidth * 0.56);
    doc.text(wrappedValue, left + 78, y);
    y += Math.max(12, wrappedValue.length * 10);
  });

  doc.setDrawColor(220, 225, 240);
  doc.line(left, y, right, y);
  y += 10;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(31, 38, 65);
  doc.text('Package Limits', left, y);
  y += 10;

  data.limits.forEach(([label, value]) => {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.text(label, left, y);
    doc.text(value, right, y, { align: 'right' });
    y += 12;
  });

  doc.setDrawColor(220, 225, 240);
  doc.line(left, y, right, y);
  y += 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('Created On:', left, y);
  doc.setFont('helvetica', 'bold');
  doc.text(data.createdOn, right, y, { align: 'right' });
  y += 11;
  doc.setFont('helvetica', 'normal');
  doc.text('Updated On:', left, y);
  doc.setFont('helvetica', 'bold');
  doc.text(data.updatedOn, right, y, { align: 'right' });
  y += 12;

  doc.setDrawColor(220, 225, 240);
  doc.line(left, y, right, y);
  y += 10;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8.5);
  doc.text('Remarks', left, y);
  y += 10;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const wrappedRemarks = doc.splitTextToSize(data.remarks || 'N/A', contentWidth);
  doc.text(wrappedRemarks, left, y);
  y += Math.max(12, wrappedRemarks.length * 10);

  doc.setDrawColor(220, 225, 240);
  doc.line(left, y, right, y);
  y += 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(95, 111, 150);
  doc.text('System-generated receipt', receiptWidth / 2, y, { align: 'center' });
  y += 10;
  doc.setFontSize(6.7);
  doc.text('Generated By: HTALLY', receiptWidth / 2, y, { align: 'center' });

  return { doc, fileName };
}

export function buildAssignPackageReceiptData(
  assignment: AssignPackageRecord,
  helpers: {
    formatPrice: (value: number) => string;
    formatDate: (value: string | null | undefined) => string;
    formatDateTime: (value: string | null | undefined) => string;
    formatStorage: (value: number) => string;
    durationLabel: (value: number) => string;
    remarksLabel: (value: string | null | undefined) => string;
  }
): AssignPackageReceiptData {
  const userName = `${assignment.appUser?.organizationName ?? assignment.appUser?.name ?? ''}`.trim() || `User #${assignment.userId}`;
  return {
    transactionId: `${assignment.id ?? 'N/A'}`,
    packageName: assignment.packageName || 'N/A',
    price: helpers.formatPrice((assignment.durationQuantity || 0) * Number(assignment.price || 0)),
    isTrial: Boolean(assignment.isTrial),
    durationLabel: `${helpers.durationLabel(assignment.durationUnite)} x ${assignment.durationQuantity || 0}`,
    expireDate: helpers.formatDate(assignment.expireDate),
    userName,
    email: `${assignment.appUser?.email ?? ''}`.trim() || 'N/A',
    mobile: `${assignment.appUser?.mobileNumber ?? ''}`.trim() || 'N/A',
    remarks: helpers.remarksLabel(assignment.remarks),
    limits: [
      ['Flat', `${assignment.maxActiveFlat || 0}`],
      ['Renter', `${assignment.maxActiveRenter || 0}`],
      ['Subuser', `${assignment.maxActiveEmployee || 0}`],
      ['File Storage', helpers.formatStorage(assignment.maxFileStorage)]
    ],
    createdOn: helpers.formatDateTime(assignment.createdOn),
    updatedOn: helpers.formatDateTime(assignment.updatedOn)
  };
}
